import { Duration } from 'luxon';
import { ReactNode, useMemo } from 'react';
import type { ImageFit } from 'yet-another-react-lightbox';
import { PhotoDTO, VideoDTO } from '../../carrier/loads/data/LoadDTO';
import { zip, type AsyncZippable, type FlateCallback } from 'fflate';
import { logWarning } from 'shared/helpers/Logger';
import { downloadBlob } from 'shared/helpers/FileHelpers';

export interface InlinePhoto {
  id: string;
  url: string;
  thumbnail_url: string;
  is_vehicle_detected: boolean | null;
  is_video: boolean;
  duration_in_seconds?: number;
}

function parsePhotos(
  photos: Array<{
    id: string;
    url: string;
    thumbnail_url: string;
    is_vehicle_detected: boolean | null;
  }>,
): InlinePhoto[] {
  return photos.map((photo) => ({
    ...photo,
    is_video: false,
    duration_in_seconds: undefined,
  }));
}

function parseVideo(video: VideoDTO): InlinePhoto {
  return {
    id: video.guid,
    url: video.thumbnail_url,
    thumbnail_url: video.thumbnail_url,
    duration_in_seconds: video.duration_in_seconds ?? undefined,
    is_vehicle_detected: null,
    is_video: true,
  };
}

export function formatDuration(seconds: number) {
  const duration = Duration.fromObject({ seconds });
  return duration.toFormat('mm:ss');
}

export function filterMediaByType<T extends { type: string }>(
  items: T[] | undefined,
  type: string,
): T[] {
  if (!items || items.length === 0) return [];
  return items.filter((item) => item.type === type);
}

export function useInlineMedia(
  photos: PhotoDTO[],
  videos?: VideoDTO[],
): InlinePhoto[] {
  return useMemo(() => {
    const firstPhoto = parsePhotos(photos.slice(0, 1));

    const pickUpVideos = filterMediaByType(videos, 'Pickup').map(parseVideo);
    const pickUpPhotos = parsePhotos(
      filterMediaByType(photos, 'Pickup').slice(1, photos.length),
    );

    const deliveryVideos = filterMediaByType(videos, 'Delivery').map(
      parseVideo,
    );
    const deliveryPhotos = parsePhotos(filterMediaByType(photos, 'Delivery'));

    return [
      ...firstPhoto,
      ...pickUpVideos,
      ...pickUpPhotos,
      ...deliveryVideos,
      ...deliveryPhotos,
    ];
  }, [photos, videos]);
}

export function parsePhotoSlide(photo: PhotoDTO, title?: ReactNode) {
  return {
    src: photo.url,
    alt: '',
    imageFit: 'contain' as ImageFit,
    title,
    mediaType: photo.type,
  };
}

const VIDEO_MIME_TYPES = ['video/mp4', 'video/quicktime', 'video/x-msvideo'];
const IMAGE_MIME_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

interface FileData {
  blob: Blob;
  extension: string;
}

function getFileExtensionFromUrl(url: string): string | null {
  try {
    const urlObj = new URL(url);
    const urlPath = urlObj.pathname;
    const match = urlPath.match(/\.([a-z0-9]+)(?:\?|$)/i);
    return match?.[1] ? `.${match[1].toLowerCase()}` : null;
  } catch {
    return null;
  }
}

function getFileExtensionFromMimeType(mimeType: string): string {
  if (VIDEO_MIME_TYPES.includes(mimeType)) {
    if (mimeType === 'video/mp4') return '.mp4';
    if (mimeType === 'video/quicktime') return '.mov';
    if (mimeType === 'video/x-msvideo') return '.avi';
  }

  if (IMAGE_MIME_TYPES.includes(mimeType)) {
    if (mimeType === 'image/jpeg' || mimeType === 'image/jpg') return '.jpg';
    if (mimeType === 'image/png') return '.png';
    if (mimeType === 'image/webp') return '.webp';
  }

  return '.bin';
}

async function fetchFileData(url: string): Promise<FileData> {
  const response = await fetch(url, { cache: 'no-cache' });

  if (!response.ok) {
    throw new Error(`Failed to fetch file: ${response.statusText}`);
  }

  const blob = await response.blob();
  const mimeType = blob.type || response.headers.get('content-type') || '';

  const extensionFromUrl = getFileExtensionFromUrl(url);
  const extensionFromMime = getFileExtensionFromMimeType(mimeType);

  const extension = extensionFromUrl || extensionFromMime;

  return { blob, extension };
}

export async function downloadMediaAsZip(
  fileUrls: string[],
  folder: string,
  zipFileName: string,
): Promise<void> {
  try {
    const fileDataPromises: Array<Promise<FileData>> = fileUrls.map((url) =>
      fetchFileData(url),
    );
    const fileDataArray = await Promise.all(fileDataPromises);

    const files: AsyncZippable = {};

    await Promise.all(
      fileDataArray.map(async (fileData, index) => {
        const { blob, extension } = fileData;
        const fileName = `${folder}/${folder}-${index + 1}${extension}`;
        const arrayBuffer = await blob.arrayBuffer();
        files[fileName] = new Uint8Array(arrayBuffer);
      }),
    );

    await new Promise<void>((resolve, reject) => {
      const callback: FlateCallback = (err, data) => {
        if (err) {
          logWarning('Failed to generate zip file', { error: err });
          reject(err);
          return;
        }

        try {
          const zipBlob = new Blob([data], { type: 'application/zip' });
          downloadBlob(zipBlob, zipFileName);
          resolve();
        } catch (error) {
          logWarning('Failed to save zip file', { error });
          reject(error);
        }
      };

      zip(files, callback);
    });
  } catch (error) {
    logWarning('Failed to generate zip file', { error });
    throw error;
  }
}
